const MAX_SYMBOLS_ROLE_PATTERN = /^alerts::max-symbols::(\d+)$/;

/**
 * Parse the maximum allowed unique symbols from a list of role strings.
 * Returns undefined if no matching role exists (meaning unlimited).
 * If multiple matching roles exist, returns the largest N.
 */
export function parseMaxSymbolsFromRoles(roles: readonly string[]): number | undefined {
    let max: number | undefined;
    for (const role of roles) {
        const match = MAX_SYMBOLS_ROLE_PATTERN.exec(role);
        if (match) {
            const n = parseInt(match[1], 10);
            if (max === undefined || n > max) {
                max = n;
            }
        }
    }
    return max;
}

interface AlertLike {
    readonly id: string;
    readonly instrumentString: string;
    readonly enabled: { readonly value: boolean };
}

/**
 * Returns unique instrument strings from enabled alerts in sorted order,
 * preserving first-occurrence order. Order matters: the first N unique symbols are
 * the ones that are "kept" when enforcing a limit.
 */
export function getActiveSymbols(sortedAlerts: readonly AlertLike[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const alert of sortedAlerts) {
        if (alert.enabled.value) {
            const sym = alert.instrumentString;
            if (!seen.has(sym)) {
                seen.add(sym);
                result.push(sym);
            }
        }
    }
    return result;
}

/**
 * Returns true if enabling an alert for the given instrument would be allowed.
 * - No limit (maxSymbols undefined): always allowed
 * - Symbol already active: always allowed (same symbol, doesn't consume a new slot)
 * - Fewer active symbols than limit: allowed
 * - At the limit with a new symbol: blocked
 */
export function canEnableAlert(
    instrumentString: string,
    activeSymbols: readonly string[],
    maxSymbols: number | undefined,
): boolean {
    if (maxSymbols === undefined) return true;
    if (activeSymbols.includes(instrumentString)) return true;
    return activeSymbols.length < maxSymbols;
}

/**
 * Walk the sorted alert list top-to-bottom. Collect the first `maxSymbols` unique
 * symbols as the "kept" set. Return IDs of all enabled alerts whose symbol is NOT
 * in the kept set — these should be disabled to enforce the limit.
 */
export function getAlertIdsToDisable(sortedAlerts: readonly AlertLike[], maxSymbols: number): string[] {
    // Collect the first maxSymbols unique symbols (in sort order)
    const keptSymbols = new Set<string>();
    for (const alert of sortedAlerts) {
        if (keptSymbols.size >= maxSymbols) break;
        if (alert.enabled.value) {
            keptSymbols.add(alert.instrumentString);
        }
    }

    // Any enabled alert whose symbol is outside the kept set must be disabled
    const toDisable: string[] = [];
    for (const alert of sortedAlerts) {
        if (alert.enabled.value && !keptSymbols.has(alert.instrumentString)) {
            toDisable.push(alert.id);
        }
    }
    return toDisable;
}
